import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, Row } from 'antd';
import React, { useEffect, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputNumberComponent from 'components/atoms/InputNumberComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import FormWrapper from 'components/molecules/FormWrapper';

import { IDodatekDVP, IDodatekIsins, dokumentUcetDvpGet, dokumentUcetDvpSend } from 'services/dokumenty/dodatek';

import { ETypZpusobPredani } from 'enums/dokumentyEnums';

import {
    protiZaplaceniDltMenaSelect,
    protiZaplaceniMenaSelect,
    protiZaplaceniTuzemskaMenaSelect
} from 'data/codebooks/dokumenty/protizaplaceniMenaSelect';

import { cisloUctuValidation, kodBankyValidation, predcisliValidation } from 'utils/validations/bankUcetValidation';
import bicValidation from 'utils/validations/bicValidation';
import ibanValidation from 'utils/validations/ibanValidation';

interface IProtiZaplaceni {
    nextStep: () => void;
    prevStep: () => void;
    zadostID: number;
    signingDisabled: boolean;
    dodatekData: IDodatekIsins;
    isDLT?: boolean;
}

const ProtiZaplaceni: React.FC<IProtiZaplaceni> = ({
    nextStep,
    prevStep,
    zadostID,
    signingDisabled,
    dodatekData,
    isDLT
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState<boolean>(false);
    const [bicErr, setBicErr] = useState<boolean>(false);
    const [, rerender] = useState<boolean>(false);
    const [data, setData] = useState<IDodatekDVP>();
    const [adresy, setAdresy] = useState<string[]>(['']);

    useEffect(() => {
        setLoading(true);
        dokumentUcetDvpGet(zadostID)
            .then(resp => {
                setData(resp);
                const adresyArr = [];
                resp.adrprij1 && adresyArr.push(resp.adrprij1);
                resp.adrprij2 && adresyArr.push(resp.adrprij2);
                resp.adrprij3 && adresyArr.push(resp.adrprij3);
                adresyArr.length && setAdresy(adresyArr);
                form.setFieldsValue(resp);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zadostID]);

    return (
        <LoadingWrapper loading={loading}>
            {!loading && (
                <FormWrapper
                    outForm={form}
                    onFinish={values => {
                        if (
                            values.typplatby === 'TUP' ||
                            (!values.bicprij && values.nazprij && values.adrprij1) ||
                            (values.bicprij && !values.nazprij && !values.adrprij1) ||
                            values.bankucet === 'PRIKAZ'
                        ) {
                            setLoading(true);
                            const adresyObj: { [key: string]: string } = {};
                            adresy.length &&
                                adresy.map(
                                    (_e, i) =>
                                        (adresyObj[`adrprij${i + 1}` as keyof typeof adresyObj] =
                                            values[`adrprij${i + 1}`])
                                );
                            dokumentUcetDvpSend(
                                zadostID,
                                {
                                    ...(values as IDodatekDVP),
                                    ...adresyObj,
                                    ...(values.iban && {
                                        iban: values.iban.split(' ').join('')
                                    })
                                },
                                'PUT'
                            )
                                .then(() => nextStep())
                                .finally(() => setLoading(false));
                        } else {
                            setBicErr(true);
                        }
                    }}
                >
                    <Title text="DOKUMENT_DODATEK_DVP_TITLE" level={5} />
                    {(dodatekData.zpuspredfp === ETypZpusobPredani.CDCP ||
                        dodatekData.zpuspredvp === ETypZpusobPredani.CDCP) && (
                        <>
                            <AnchorComponent
                                id="downloadPlnaMocBtn"
                                href={
                                    window.location.href.includes('test')
                                        ? 'https://test.cdcp.cz/wp-content/uploads/md/Seznam_uctu.pdf'
                                        : 'https://www.cdcp.cz/wp-content/uploads/md/Seznam_uctu.pdf'
                                }
                                download="Seznam_uctu.pdf"
                                target="_blank"
                            >
                                <ButtonComponent
                                    buttontext="DOKUMENT_DODATEK_DVP_POCET_UPISOVATELU_DOWNLOAD"
                                    htmlType="button"
                                    id="isinDLBtn"
                                />
                            </AnchorComponent>

                            <Paragraph
                                text="DOKUMENT_DODATEK_DVP_POCET_UPISOVATELU_TEXT"
                                style={{
                                    display: 'block',
                                    marginBottom: 20
                                }}
                            />
                        </>
                    )}
                    <SelectComponent
                        isFormItem={true}
                        data={[
                            {
                                labelcz: 'PLATBA_TUP',
                                value: 'TUP'
                            },
                            ...(!isDLT
                                ? [
                                      {
                                          labelcz: 'PLATBA_ZAP',
                                          value: 'ZAP'
                                      }
                                  ]
                                : [])
                        ]}
                        name={'typplatby'}
                        labelParagraph={'TYP_PLATBY'}
                        defaultValue={isDLT ? 'TUP' : data?.typplatby}
                        hide={isDLT}
                        onChange={val => {
                            form.setFieldsValue({
                                mena: val === 'TUP' ? 'CZK' : undefined
                            });
                            rerender(pv => !pv);
                        }}
                    />
                    {form.getFieldValue('typplatby') && (
                        <SelectComponent
                            name={'mena'}
                            labelParagraph="MENA_VYPORADANI"
                            isFormItem={true}
                            data={
                                form.getFieldValue('typplatby') === 'TUP'
                                    ? protiZaplaceniTuzemskaMenaSelect
                                    : isDLT
                                      ? protiZaplaceniDltMenaSelect
                                      : protiZaplaceniMenaSelect
                            }
                            defaultValue={form.getFieldValue('typplatby') === 'TUP' ? 'CZK' : data?.mena}
                            disabled={signingDisabled || form.getFieldValue('typplatby') === 'TUP'}
                            onChange={() => rerender(pv => !pv)}
                        />
                    )}
                    <SelectComponent
                        isFormItem={true}
                        data={[
                            {
                                labelcz: 'DOKUMENT_DODATEK_DVP_BANKUCET_NYNI',
                                value: 'NYNI'
                            },
                            ...(!isDLT
                                ? [
                                      {
                                          labelcz: 'DOKUMENT_DODATEK_DVP_BANKUCET_PRIKAZ',
                                          value: 'PRIKAZ'
                                      }
                                  ]
                                : [])
                        ]}
                        name="bankucet"
                        labelParagraph={'DOKUMENT_DODATEK_DVP_BANKUCET'}
                        onChange={() => {
                            rerender(pv => !pv);
                        }}
                        defaultValue={isDLT ? 'NYNI' : data?.bankucet}
                        hide={isDLT}
                        disabled={signingDisabled}
                    />
                    {form.getFieldValue('bankucet') === 'NYNI' && (
                        <>
                            {dodatekData.zpuspredvp !== ETypZpusobPredani.BURZA && (
                                <InputNumberComponent
                                    name="celkcastka"
                                    label="DOKUMENT_DODATEK_DVP_CASTKA_CP"
                                    initialValue={data?.celkcastka}
                                    maxLength={12}
                                    precision={2}
                                    disabled={signingDisabled}
                                />
                            )}

                            {/* <InputNumberComponent
                                label="DOKUMENT_DODATEK_DVP_POCET_UPISOVATELU"
                                name="celkpocupis"
                                initialValue={data?.celkpocupis}
                                precision={0}
                                disabled={signingDisabled}
                            />*/}
                            <Title
                                level={5}
                                text="DOKUMENT_DODATEK_DVP_CASTKA_TITLE"
                                style={{
                                    display: 'inline-block'
                                }}
                                dividerTitle={true}
                            />

                            {form.getFieldValue('typplatby') === 'TUP' && (
                                <>
                                    <InputComponent
                                        label="PREDCISLI_UCTU"
                                        name="predcisli"
                                        initialValue={data?.predcisli}
                                        notRequired={true}
                                        maxLength={6}
                                        rules={predcisliValidation()}
                                        disabled={signingDisabled}
                                    />
                                    <InputComponent
                                        label="CISLO_UCTU"
                                        name="cislouctu"
                                        initialValue={data?.cislouctu}
                                        maxLength={10}
                                        rules={cisloUctuValidation()}
                                        disabled={signingDisabled}
                                    />
                                    <InputComponent
                                        label="CISLO_BANKY"
                                        name="banka"
                                        initialValue={data?.banka}
                                        maxLength={4}
                                        rules={kodBankyValidation()}
                                        disabled={signingDisabled}
                                    />
                                </>
                            )}
                            {form.getFieldValue('typplatby') === 'ZAP' && (
                                <>
                                    <InputComponent
                                        label="IBAN_PRIJEMCE"
                                        name={'iban'}
                                        maxLength={250}
                                        rules={form.getFieldValue('mena') === 'EUR' ? ibanValidation() : undefined}
                                        initialValue={data?.iban}
                                        disabled={signingDisabled}
                                    />
                                    <InputComponent
                                        label="BIC_BANKY"
                                        name={'bic'}
                                        initialValue={data?.bic}
                                        rules={bicValidation()}
                                        disabled={signingDisabled}
                                    />
                                    <InputComponent
                                        label="NAZEV_PRIJEMCE"
                                        name={'nazprij'}
                                        initialValue={data?.nazprij}
                                        disabled={signingDisabled}
                                        onChange={() => setBicErr(false)}
                                        notRequired={true}
                                    />
                                    {adresy.map((e, i) => (
                                        <InputComponent
                                            label="ADRESA_PRIJEMCE"
                                            name={`adrprij${i + 1}`}
                                            key={`adrprij${i + 1}`}
                                            maxLength={35}
                                            notRequired={true}
                                            disabled={signingDisabled}
                                            onChange={() => setBicErr(false)}
                                            initialValue={e}
                                            labelExtra={
                                                (i === adresy.length - 1 || (i === 0 && adresy.length < 3)) &&
                                                !signingDisabled ? (
                                                    <Button
                                                        id="c2PlusAltBtn"
                                                        htmlType="button"
                                                        type="primary"
                                                        style={{
                                                            display: 'inline-block',
                                                            marginLeft: 20,
                                                            width: 50
                                                        }}
                                                        disabled={false}
                                                        icon={i === 0 ? <PlusOutlined /> : <MinusOutlined />}
                                                        onClick={() => {
                                                            if (i === 0) setAdresy(pv => [...pv, '']);
                                                            else {
                                                                form.setFieldsValue({
                                                                    [`adrprij${i + 1}`]: undefined
                                                                });
                                                                setAdresy(pv => pv.filter((_e, index) => i !== index));
                                                            }
                                                        }}
                                                    />
                                                ) : null
                                            }
                                        />
                                    ))}
                                    <InputComponent
                                        label="PRIJEMCE_BIC"
                                        name={'bicprij'}
                                        initialValue={data?.bicprij}
                                        rules={bicValidation()}
                                        disabled={signingDisabled}
                                        onChange={() => setBicErr(false)}
                                        notRequired={true}
                                    />
                                </>
                            )}
                        </>
                    )}

                    <Row className="button-row">
                        <ButtonComponent buttontext="ZPET" onClick={() => prevStep()} id="isinZpetBtn" type="default" />
                        <ButtonComponent
                            buttontext="POKRACOVAT"
                            htmlType="submit"
                            id="isinPokracovatBtn"
                            extramessage={bicErr ? 'DOKUMENT_DODATEK_DVP_ADRESA_BIC_ERR' : ''}
                        />
                    </Row>
                </FormWrapper>
            )}
        </LoadingWrapper>
    );
};

export default ProtiZaplaceni;
